/**
 * Membership settings API wrapper (v2 — Project Independence, DNKADM-0056 U2).
 *
 * The single one-row-per-tenant membership-policy config (handoff §5.10):
 * the additive-vs-replace credit refill toggle, the grace window, the reschedule
 * cap, and the refund-rounding step + mode. Bearer-only `/v2/admin/**` surface —
 * `request()` routes it (DNKADM-0049 `isV2Surface`): bearer token, NO CSRF, NO
 * cookies, the bounded 401→refresh→retry; the mutating-audit `X-Session-Mode` stamp
 * fires on the PUT.
 *
 * **Re-point from v1 (§9):** the v1 `creditsPerHour` (credit valuation — now an
 * explicit per-variation `creditCost`, 0055) and the global daily cap
 * (`dailySchedulingCapHours` — now the per-(plan,bucket) daily-cap matrix, 0057) are
 * GONE. This wrapper carries ONLY the new field set; it neither reads nor writes the
 * retired fields.
 *
 * **`v2_membership_settings` field set (handoff §5.10):**
 *  - `creditsAdditive` (boolean, default **true**) — additive (each refill stacks a
 *    fresh lot) vs replace (zero existing lots, grant one fresh lot).
 *  - `graceDays` (integer ≥ 0, default 0).
 *  - `maxReschedules` (integer ≥ 0; **null/0 = unlimited**).
 *  - `refundRoundingIncrement` (DECIMAL(10,2) > 0, default 0.5).
 *  - `refundRoundingMode` (a `java.math.RoundingMode` name, default `HALF_UP`).
 *  - `blockMembershipCreditsOnPaymentFailure` (boolean, default **true**, DNKAPI-0704) —
 *    while a member's membership payment has failed, their own bookings can't spend
 *    membership credits. Optional on the PUT (omitted = the stored value is kept).
 *
 *  - `GET /v2/admin/membership-settings` → {@link MembershipSettingsView}.
 *  - `PUT /v2/admin/membership-settings` ← {@link MembershipSettingsUpdate}.
 *
 * GET parsing is DEFENSIVE (mirrors the v1 wrapper / `services.ts`): a missing or
 * wrong-typed field degrades to the documented server default, never a throw.
 *
 * `refundRoundingMode` allowed set: the full `java.math.RoundingMode` name set —
 * `UP | DOWN | CEILING | FLOOR | HALF_UP | HALF_DOWN | HALF_EVEN | UNNECESSARY`. The
 * server is the authority — an off-list string is rejected with a `400` the UI surfaces
 * inline (see `MembershipSettingsCard`).
 */
import { request } from './client';

/** v2 admin membership-settings base path (bearer surface). */
const PATH = '/v2/admin/membership-settings';

/**
 * The full `java.math.RoundingMode` name set the backend accepts/round-trips. The
 * picker offers a subset (see `REFUND_ROUNDING_MODES`); every name here still parses
 * on read.
 */
export type RefundRoundingMode =
  | 'UP'
  | 'DOWN'
  | 'CEILING'
  | 'FLOOR'
  | 'HALF_UP'
  | 'HALF_DOWN'
  | 'HALF_EVEN'
  | 'UNNECESSARY';

/**
 * Every valid `RoundingMode` name — the complete parse/round-trip set. A stored value
 * in this set renders unchanged; only a genuinely unknown value degrades. This is the
 * validation set for {@link asRefundRoundingMode}, kept complete even when a mode is
 * not offered in the picker (see `REFUND_ROUNDING_MODES`).
 */
const REFUND_ROUNDING_MODES_ALL: readonly RefundRoundingMode[] = [
  'UP',
  'DOWN',
  'CEILING',
  'FLOOR',
  'HALF_UP',
  'HALF_DOWN',
  'HALF_EVEN',
  'UNNECESSARY',
];

/**
 * The modes the UI OFFERS in the picker, in display order; the segmented control
 * renders one button each. `UNNECESSARY` is intentionally NOT offered (it makes the
 * server reject any refund that doesn't already land exactly on a step, a foot-gun in
 * the admin console). A stored `UNNECESSARY` value still parses and renders.
 */
export const REFUND_ROUNDING_MODES: readonly RefundRoundingMode[] = [
  'UP',
  'DOWN',
  'CEILING',
  'FLOOR',
  'HALF_UP',
  'HALF_DOWN',
  'HALF_EVEN',
];

/**
 * Parsed `GET /v2/admin/membership-settings` (§5.10). All fields resolve server-side
 * to concrete values; we still parse defensively and fall back to the documented
 * defaults if a field is missing/mistyped.
 */
export interface MembershipSettingsView {
  /**
   * Credit-refill semantics: `true` (default) = **additive** (each refill stacks a
   * fresh lot; leftovers carry until expiry); `false` = **replace** (zero existing
   * lots, grant one fresh lot — no carryover).
   */
  creditsAdditive: boolean;
  /** Expiry grace window in days (integer ≥ 0; default 0). */
  graceDays: number;
  /**
   * Per-tenant cap on how many times a member/guest may reschedule a single
   * reservation (integer ≥ 0; `null` = unlimited). Absent/wrong-typed/negative on the
   * wire degrades to `null` (unlimited).
   */
  maxReschedules: number | null;
  /** Partial refunds round to this step (> 0; default 0.5). */
  refundRoundingIncrement: number;
  /** Rounding direction for partial refunds (default `HALF_UP`). */
  refundRoundingMode: RefundRoundingMode;
  /**
   * Pause membership-credit booking while a member's membership payment has failed
   * (DNKAPI-0704; default **true**, also when an older server omits it).
   */
  blockMembershipCreditsOnPaymentFailure: boolean;
}

/**
 * `PUT /v2/admin/membership-settings` input (§5.10). The form sends the full record.
 * Ranges (server + client): `graceDays` integer ≥ 0, `refundRoundingIncrement` > 0,
 * `refundRoundingMode` in the backend's allowed set, `maxReschedules` integer ≥ 0
 * (the form sends `0` for unlimited).
 */
export interface MembershipSettingsUpdate {
  creditsAdditive?: boolean;
  graceDays?: number;
  /** Reschedule cap (integer ≥ 0); the form sends `0` for unlimited. */
  maxReschedules?: number;
  refundRoundingIncrement?: number;
  refundRoundingMode?: string;
  /** Omitted = the stored value is kept (DNKAPI-0704). */
  blockMembershipCreditsOnPaymentFailure?: boolean;
}

// --- Defensive parsing helpers (mirrors services.ts / the v1 wrapper) -----------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized as text), or `null`. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Normalize the refund-rounding mode against the full `RoundingMode` name set. Every
 * value the backend can store is in the set, so a valid stored mode round-trips
 * unchanged; only a genuinely absent/unrecognized value degrades — to `HALF_UP`,
 * matching the server's own fallback.
 */
function asRefundRoundingMode(value: unknown): RefundRoundingMode {
  return REFUND_ROUNDING_MODES_ALL.includes(value as RefundRoundingMode)
    ? (value as RefundRoundingMode)
    : 'HALF_UP';
}

/**
 * The reschedule cap: an integer ≥ 0, or `null` = unlimited. Forward-compatible — a
 * field that's absent, wrong-typed, or negative degrades to `null` (unlimited). A
 * present non-negative value is normalized to a whole number.
 */
function asMaxReschedules(value: unknown): number | null {
  const n = asNumber(value);
  if (n === null || n < 0) return null;
  return Math.trunc(n);
}

function parseSettings(raw: unknown): MembershipSettingsView {
  const o = isObject(raw) ? raw : {};
  return {
    creditsAdditive: asBoolean(o['creditsAdditive'], true),
    graceDays: asNumber(o['graceDays']) ?? 0,
    maxReschedules: asMaxReschedules(o['maxReschedules']),
    refundRoundingIncrement: asNumber(o['refundRoundingIncrement']) ?? 0.5,
    refundRoundingMode: asRefundRoundingMode(o['refundRoundingMode']),
    blockMembershipCreditsOnPaymentFailure: asBoolean(
      o['blockMembershipCreditsOnPaymentFailure'],
      true,
    ),
  };
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/membership-settings` → the tenant's resolved membership settings.
 * Defensively parsed (a missing field degrades to the documented default, never
 * throws). Throws `ApiError` on a non-2xx response.
 */
export async function getMembershipSettings(): Promise<MembershipSettingsView> {
  const body = await request<unknown>('GET', PATH);
  return parseSettings(body);
}

/**
 * `PUT /v2/admin/membership-settings` → save the membership settings. The form sends
 * the full record; range validation runs client-side first (mirroring the server) so
 * a well-formed request is the norm. Returns the re-parsed view. Throws a typed
 * `ApiError`: `400` VALIDATION (a bad range or an unexpected rounding mode — surfaced
 * inline), `403` FORBIDDEN, and forwards upstream failures.
 */
export async function putMembershipSettings(
  input: MembershipSettingsUpdate,
): Promise<MembershipSettingsView> {
  const json: Record<string, unknown> = {};
  if (input.creditsAdditive !== undefined) json['creditsAdditive'] = input.creditsAdditive;
  if (input.graceDays !== undefined) json['graceDays'] = input.graceDays;
  if (input.maxReschedules !== undefined) json['maxReschedules'] = input.maxReschedules;
  if (input.refundRoundingIncrement !== undefined) {
    json['refundRoundingIncrement'] = input.refundRoundingIncrement;
  }
  if (input.refundRoundingMode !== undefined) {
    json['refundRoundingMode'] = input.refundRoundingMode;
  }
  if (input.blockMembershipCreditsOnPaymentFailure !== undefined) {
    json['blockMembershipCreditsOnPaymentFailure'] = input.blockMembershipCreditsOnPaymentFailure;
  }

  const body = await request<unknown>('PUT', PATH, { json });
  return parseSettings(body);
}
